'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Users, BarChart2, Send, RefreshCw, Plus, Search, ChevronDown, ChevronUp, CheckCircle2, Clock, AlertCircle, Archive, Zap, ExternalLink, List, FileText, TrendingUp, Eye, MousePointerClick, XCircle, Loader2, Inbox, Webhook, ShieldCheck } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

interface BrevoAccount {
  email: string;
  firstName: string;
  lastName: string;
  companyName: string;
  plan: Array<{ type: string; credits: number; creditsType: string }>;
}

interface BrevoStats {
  globalStats?: {
    uniqueClicks: number;
    clickers: number;
    complaints: number;
    delivered: number;
    sent: number;
    softBounces: number;
    hardBounces: number;
    uniqueViews: number;
    unsubscriptions: number;
    viewed: number;
  };
}

interface BrevoSender {
  id?: number;
  name: string;
  email: string;
}

interface BrevoRecipients {
  lists?: number[];
  exclusionLists?: number[];
}

interface BrevoEmailCampaign {
  id: number;
  name: string;
  subject: string;
  type: string;
  status: string;
  scheduledAt?: string;
  createdAt: string;
  modifiedAt: string;
  sender: BrevoSender;
  recipients?: BrevoRecipients;
  statistics?: BrevoStats;
}

interface BrevoContact {
  id: number;
  email: string;
  emailBlacklisted: boolean;
  smsBlacklisted: boolean;
  createdAt: string;
  modifiedAt: string;
  attributes?: Record<string, any>;
  listIds?: number[];
}

interface BrevoList {
  id: number;
  name: string;
  totalBlacklisted: number;
  totalSubscribers: number;
  folderId: number;
  createdAt: string;
  campaignStats?: Array<{ campaignId: number; stats: any }>;
}

interface BrevoTemplate {
  id: number;
  name: string;
  subject: string;
  isActive: boolean;
  testSent: boolean;
  sender: BrevoSender;
  replyTo: string;
  toField: string;
  tag: string;
  createdAt: string;
  modifiedAt: string;
}

type TabKey = 'overview' | 'campaigns' | 'contacts' | 'lists' | 'templates';

// ─── Status helpers ───────────────────────────────────────────────────────────

const statusConfig: Record<string, { label: string; variant: 'success' | 'warning' | 'neutral' | 'primary' | 'info'; icon: React.ElementType }> = {
  sent: { label: 'Sent', variant: 'success', icon: CheckCircle2 },
  draft: { label: 'Draft', variant: 'neutral', icon: FileText },
  queued: { label: 'Queued', variant: 'warning', icon: Clock },
  inProcess: { label: 'Sending', variant: 'warning', icon: Zap },
  inReview: { label: 'In Review', variant: 'info', icon: Eye },
  suspended: { label: 'Suspended', variant: 'neutral', icon: XCircle },
  archive: { label: 'Archived', variant: 'info', icon: Archive },
};

function CampaignStatusBadge({ status }: { status: string }) {
  const cfg = statusConfig[status] ?? { label: status, variant: 'neutral' as const, icon: AlertCircle };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold
      ${cfg.variant === 'success' ? 'bg-green-100 text-green-700' :
        cfg.variant === 'warning' ? 'bg-amber-100 text-amber-700' :
        cfg.variant === 'primary' ? 'bg-violet-100 text-violet-700' :
        cfg.variant === 'info'? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
      <Icon size={11} />
      {cfg.label}
    </span>
  );
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color }: { label: string; value: string | number; icon: React.ElementType; color: string }) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={18} className="text-white" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground font-medium">{label}</p>
        <p className="text-xl font-bold text-foreground">{value}</p>
      </div>
    </div>
  );
}

// ─── Create Campaign Modal ────────────────────────────────────────────────────

interface CreateCampaignModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  lists: BrevoList[];
}

function CreateCampaignModal({ open, onClose, onCreated, lists }: CreateCampaignModalProps) {
  const [form, setForm] = useState({
    name: '',
    subject: '',
    senderName: '',
    senderEmail: '',
    htmlContent: '<p>Hello {{contact.FIRSTNAME}},</p><p>Your email content here.</p>',
    listId: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.subject || !form.senderEmail || !form.senderName) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const payload: any = {
        name: form.name,
        subject: form.subject,
        sender: { name: form.senderName, email: form.senderEmail },
        htmlContent: form.htmlContent,
      };
      if (form.listId) payload.recipients = { listIds: [parseInt(form.listId)] };

      const res = await fetch('/api/brevo/campaigns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create campaign');
      onCreated();
      onClose();
      setForm({ name: '', subject: '', senderName: '', senderEmail: '', htmlContent: '<p>Hello {{contact.FIRSTNAME}},</p><p>Your email content here.</p>', listId: '' });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Create Email Campaign" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-foreground mb-1">Campaign Name *</label>
            <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Summer Newsletter 2026" />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-foreground mb-1">Email Subject *</label>
            <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} placeholder="e.g. Exclusive offer just for you 🎉" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Sender Name *</label>
            <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.senderName} onChange={e => setForm(p => ({ ...p, senderName: e.target.value }))} placeholder="e.g. AppNest Team" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Sender Email *</label>
            <input type="email" className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.senderEmail} onChange={e => setForm(p => ({ ...p, senderEmail: e.target.value }))} placeholder="hello@yourdomain.com" />
          </div>
          {lists.length > 0 && (
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-foreground mb-1">Recipient List (optional)</label>
              <select className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
                value={form.listId} onChange={e => setForm(p => ({ ...p, listId: e.target.value }))}>
                <option value="">— Select a list —</option>
                {lists.map(l => <option key={l.id} value={l.id}>{l.name} ({l.totalSubscribers} subscribers)</option>)}
              </select>
            </div>
          )}
          <div className="col-span-2">
            <label className="block text-xs font-semibold text-foreground mb-1">HTML Content</label>
            <textarea rows={5} className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background font-mono focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.htmlContent} onChange={e => setForm(p => ({ ...p, htmlContent: e.target.value }))} />
            <p className="text-xs text-muted-foreground mt-1">Use <code className="bg-muted px-1 rounded">{'{{contact.FIRSTNAME}}'}</code> for personalization.</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors">Cancel</button>
          <button type="submit" disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-2 transition-all disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {loading ? 'Creating…' : 'Create Campaign'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Add Contact Modal ────────────────────────────────────────────────────────

interface AddContactModalProps {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
  lists: BrevoList[];
}

function AddContactModal({ open, onClose, onAdded, lists }: AddContactModalProps) {
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', listId: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email) { setError('Email is required.'); return; }
    setLoading(true);
    setError('');
    try {
      const payload: any = {
        email: form.email,
        attributes: { FIRSTNAME: form.firstName, LASTNAME: form.lastName },
        updateEnabled: true,
      };
      if (form.listId) payload.listIds = [parseInt(form.listId)];

      const res = await fetch('/api/brevo/contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to add contact');
      onAdded();
      onClose();
      setForm({ email: '', firstName: '', lastName: '', listId: '' });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Add Contact" size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1">Email Address *</label>
          <input type="email" className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
            value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="contact@example.com" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">First Name</label>
            <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.firstName} onChange={e => setForm(p => ({ ...p, firstName: e.target.value }))} placeholder="John" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Last Name</label>
            <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.lastName} onChange={e => setForm(p => ({ ...p, lastName: e.target.value }))} placeholder="Doe" />
          </div>
        </div>
        {lists.length > 0 && (
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">Add to List (optional)</label>
            <select className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
              value={form.listId} onChange={e => setForm(p => ({ ...p, listId: e.target.value }))}>
              <option value="">— Select a list —</option>
              {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors">Cancel</button>
          <button type="submit" disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-2 transition-all disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {loading ? 'Adding…' : 'Add Contact'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Create List Modal ────────────────────────────────────────────────────────

interface CreateListModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateListModal({ open, onClose, onCreated }: CreateListModalProps) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('List name is required.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/brevo/lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), folderId: 1 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create list');
      onCreated();
      onClose();
      setName('');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={open} onClose={onClose} title="Create Contact List" size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1">List Name *</label>
          <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500"
            value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Newsletter Subscribers" autoFocus />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors">Cancel</button>
          <button type="submit" disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white flex items-center gap-2 transition-all disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            {loading ? 'Creating…' : 'Create List'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Webhook Setup Panel ──────────────────────────────────────────────────────

interface WebhookInfo {
  id: number;
  url: string;
  type: string;
  events: string[];
  description: string;
  isOurs: boolean;
}

function WebhookSetupPanel() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'checking' | 'registered' | 'error'>('idle');
  const [webhooks, setWebhooks] = useState<WebhookInfo[]>([]);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [error, setError] = useState('');
  const [registeredType, setRegisteredType] = useState<'transactional' | 'marketing'>('transactional');

  const checkWebhooks = useCallback(async () => {
    setStatus('checking');
    setError('');
    try {
      const res = await fetch('/api/brevo/webhook/register');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to check webhooks');
      setWebhooks(data.webhooks || []);
      setAlreadyRegistered(data.alreadyRegistered || false);
      setStatus(data.alreadyRegistered ? 'registered' : 'idle');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to check webhooks';
      setError(msg);
      setStatus('error');
    }
  }, []);

  useEffect(() => { checkWebhooks(); }, [checkWebhooks]);

  const registerWebhook = async (type: 'transactional' | 'marketing') => {
    setStatus('loading');
    setError('');
    setRegisteredType(type);
    try {
      const res = await fetch('/api/brevo/webhook/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to register webhook');
      setAlreadyRegistered(true);
      setStatus('registered');
      await checkWebhooks();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to register webhook';
      setError(msg);
      setStatus('error');
    }
  };

  const ourWebhooks = webhooks.filter((w) => w.isOurs);
  const isLoading = status === 'loading' || status === 'checking';

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
        <div className="flex items-center gap-2">
          <Webhook size={15} className="text-violet-500" />
          <h3 className="text-sm font-semibold text-foreground">Webhook Event Tracking</h3>
        </div>
        <button onClick={checkWebhooks} disabled={isLoading}
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors">
          <RefreshCw size={11} className={isLoading ? 'animate-spin' : ''} />
          {isLoading ? 'Checking…' : 'Refresh'}
        </button>
      </div>

      <div className="p-5 space-y-4">
        {/* Status banner */}
        {alreadyRegistered ? (
          <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-green-50 border border-green-200">
            <ShieldCheck size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-green-800">Webhook Active</p>
              <p className="text-xs text-green-700 mt-0.5">
                Brevo is sending real-time bounce, delivered, open &amp; click events to your app. Campaign stats update automatically.
              </p>
            </div>
          </div>
        ) : (
          <div className="flex items-start gap-3 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
            <AlertCircle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800">Webhook Not Registered</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Register a webhook to receive real-time bounce, delivery, open &amp; click events from Brevo and auto-update campaign stats.
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-red-50 border border-red-200">
            <AlertCircle size={13} className="text-red-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}

        {/* Events tracked */}
        <div>
          <p className="text-xs font-semibold text-foreground mb-2">Events Tracked</p>
          <div className="flex flex-wrap gap-1.5">
            {['delivered', 'hardBounce', 'softBounce', 'opened', 'click', 'unsubscribed', 'spam'].map((evt) => (
              <span key={evt} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${
                alreadyRegistered
                  ? 'bg-green-50 text-green-700 border-green-200' :'bg-muted text-muted-foreground border-border'
              }`}>
                {alreadyRegistered && <CheckCircle2 size={9} />}
                {evt}
              </span>
            ))}
          </div>
        </div>

        {/* Registered webhooks list */}
        {ourWebhooks.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-foreground mb-2">Registered Webhooks</p>
            <div className="space-y-1.5">
              {ourWebhooks.map((wh) => (
                <div key={wh.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/40 border border-border">
                  <CheckCircle2 size={12} className="text-green-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-foreground truncate">{wh.url}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{wh.type} · ID #{wh.id}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Register buttons */}
        {!alreadyRegistered && (
          <div className="flex gap-2 pt-1">
            <button
              onClick={() => registerWebhook('transactional')}
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-60 transition-all"
              style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}
            >
              {isLoading && registeredType === 'transactional' ? <Loader2 size={12} className="animate-spin" /> : <Webhook size={12} />}
              Register Transactional
            </button>
            <button
              onClick={() => registerWebhook('marketing')}
              disabled={isLoading}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-border text-foreground hover:bg-muted disabled:opacity-60 transition-all"
            >
              {isLoading && registeredType === 'marketing' ? <Loader2 size={12} className="animate-spin" /> : <Webhook size={12} />}
              Register Marketing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Client ──────────────────────────────────────────────────────────────

export default function BrevoClient() {
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Data
  const [account, setAccount] = useState<BrevoAccount | null>(null);
  const [campaigns, setCampaigns] = useState<BrevoEmailCampaign[]>([]);
  const [campaignCount, setCampaignCount] = useState(0);
  const [contacts, setContacts] = useState<BrevoContact[]>([]);
  const [contactCount, setContactCount] = useState(0);
  const [lists, setLists] = useState<BrevoList[]>([]);
  const [templates, setTemplates] = useState<BrevoTemplate[]>([]);

  // UI state
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('all');
  const [contactSearch, setContactSearch] = useState('');
  const [expandedCampaign, setExpandedCampaign] = useState<number | null>(null);
  const [sendingCampaignId, setSendingCampaignId] = useState<number | null>(null);

  // Modals
  const [showCreateCampaign, setShowCreateCampaign] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [showCreateList, setShowCreateList] = useState(false);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/brevo/overview');
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load Brevo data');
      setAccount(data.account || null);
      setCampaigns(data.campaigns?.campaigns || []);
      setCampaignCount(data.campaigns?.count || 0);
      setContactCount(data.contacts?.count || 0);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCampaigns = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '50', statistics: 'globalStats' });
      if (campaignStatusFilter !== 'all') params.set('status', campaignStatusFilter);
      const res = await fetch(`/api/brevo/campaigns?${params}`);
      const data = await res.json();
      if (res.ok) { setCampaigns(data.campaigns || []); setCampaignCount(data.count || 0); }
    } catch {}
  }, [campaignStatusFilter]);

  const fetchContacts = useCallback(async () => {
    try {
      const res = await fetch('/api/brevo/contacts?limit=50');
      const data = await res.json();
      if (res.ok) { setContacts(data.contacts || []); setContactCount(data.count || 0); }
    } catch {}
  }, []);

  const fetchLists = useCallback(async () => {
    try {
      const res = await fetch('/api/brevo/lists?limit=20');
      const data = await res.json();
      if (res.ok) setLists(data.lists || []);
    } catch {}
  }, []);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch('/api/brevo/templates?limit=20');
      const data = await res.json();
      if (res.ok) setTemplates(data.templates || []);
    } catch {}
  }, []);

  useEffect(() => { fetchOverview(); fetchLists(); }, [fetchOverview, fetchLists]);

  useEffect(() => {
    if (activeTab === 'campaigns') fetchCampaigns();
    if (activeTab === 'contacts') fetchContacts();
    if (activeTab === 'lists') fetchLists();
    if (activeTab === 'templates') fetchTemplates();
  }, [activeTab, fetchCampaigns, fetchContacts, fetchLists, fetchTemplates]);

  useEffect(() => {
    if (activeTab === 'campaigns') fetchCampaigns();
  }, [campaignStatusFilter]);

  const handleSendNow = async (campaignId: number) => {
    setSendingCampaignId(campaignId);
    try {
      const res = await fetch(`/api/brevo/campaigns/${campaignId}/send`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send campaign');
      await fetchCampaigns();
    } catch (err: any) {
      alert(`Send failed: ${err.message}`);
    } finally {
      setSendingCampaignId(null);
    }
  };

  const filteredCampaigns = campaigns.filter(c =>
    c.name?.toLowerCase().includes(campaignSearch.toLowerCase()) ||
    c.subject?.toLowerCase().includes(campaignSearch.toLowerCase())
  );

  const filteredContacts = contacts.filter(c =>
    c.email?.toLowerCase().includes(contactSearch.toLowerCase()) ||
    (c.attributes?.FIRSTNAME + ' ' + c.attributes?.LASTNAME)?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const tabs: Array<{ key: TabKey; label: string; icon: React.ElementType }> = [
    { key: 'overview', label: 'Overview', icon: BarChart2 },
    { key: 'campaigns', label: 'Campaigns', icon: Mail },
    { key: 'contacts', label: 'Contacts', icon: Users },
    { key: 'lists', label: 'Lists', icon: List },
    { key: 'templates', label: 'Templates', icon: FileText },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #0092ff 0%, #00c4ff 100%)' }}>
              <Mail size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-foreground">Brevo Integration</h1>
              <p className="text-sm text-muted-foreground">
                {account ? `${account.email} · ${account.companyName || 'Brevo Account'}` : 'Email marketing, automation & contact management'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="https://app.brevo.com" target="_blank" rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:bg-muted transition-colors">
              <ExternalLink size={13} />
              Open Brevo
            </a>
            <button onClick={fetchOverview}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:bg-muted transition-colors">
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.key
                    ? 'text-white shadow-sm'
                    : 'text-muted-foreground hover:bg-muted'
                }`}
                style={activeTab === tab.key ? { background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' } : {}}>
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading && activeTab === 'overview' ? (
          <div className="flex items-center justify-center h-48">
            <Loader2 size={28} className="animate-spin text-violet-500" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle size={22} className="text-red-500" />
            </div>
            <div className="text-center">
              <p className="font-semibold text-foreground">Failed to connect to Brevo</p>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm">{error}</p>
            </div>
            <button onClick={fetchOverview} className="px-4 py-2 rounded-lg text-sm font-medium text-white"
              style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* ── Overview Tab ── */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* KPI Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard label="Total Campaigns" value={campaignCount} icon={Mail} color="bg-violet-500" />
                  <StatCard label="Total Contacts" value={contactCount.toLocaleString()} icon={Users} color="bg-blue-500" />
                  <StatCard label="Contact Lists" value={lists.length} icon={List} color="bg-emerald-500" />
                  <StatCard label="Plan Credits" value={account?.plan?.[0]?.credits ?? '—'} icon={Zap} color="bg-amber-500" />
                </div>

                {/* Webhook Setup Panel */}
                <WebhookSetupPanel />

                {/* Account Info */}
                {account && (
                  <div className="bg-card border border-border rounded-xl p-5">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <CheckCircle2 size={15} className="text-emerald-500" />
                      Connected Account
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">Email</p>
                        <p className="text-sm font-medium text-foreground truncate">{account.email}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Name</p>
                        <p className="text-sm font-medium text-foreground">{account.firstName} {account.lastName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Company</p>
                        <p className="text-sm font-medium text-foreground">{account.companyName || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Plan Type</p>
                        <p className="text-sm font-medium text-foreground capitalize">{account.plan?.[0]?.type || '—'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Campaigns */}
                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-border">
                    <h3 className="text-sm font-semibold text-foreground">Recent Campaigns</h3>
                    <button onClick={() => setActiveTab('campaigns')} className="text-xs text-violet-600 font-medium hover:underline">View all</button>
                  </div>
                  {campaigns.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-10 gap-2">
                      <Inbox size={28} className="text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">No campaigns yet</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {campaigns.slice(0, 5).map(c => (
                        <div key={c.id} className="flex items-center justify-between px-5 py-3 hover:bg-muted/30 transition-colors">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{c.subject}</p>
                          </div>
                          <div className="flex items-center gap-3 ml-4 flex-shrink-0">
                            {c.statistics?.globalStats && (
                              <div className="hidden md:flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1"><Send size={11} />{c.statistics.globalStats.sent ?? 0}</span>
                                <span className="flex items-center gap-1"><Eye size={11} />{c.statistics.globalStats.uniqueViews ?? 0}</span>
                                <span className="flex items-center gap-1"><MousePointerClick size={11} />{c.statistics.globalStats.uniqueClicks ?? 0}</span>
                              </div>
                            )}
                            <CampaignStatusBadge status={c.status} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { label: 'Create Campaign', desc: 'Launch a new email campaign', icon: Mail, action: () => setShowCreateCampaign(true) },
                    { label: 'Add Contact', desc: 'Add a new subscriber', icon: Users, action: () => setShowAddContact(true) },
                    { label: 'New List', desc: 'Organize your audience', icon: List, action: () => setShowCreateList(true) },
                  ].map(qa => {
                    const Icon = qa.icon;
                    return (
                      <button key={qa.label} onClick={qa.action}
                        className="flex items-center gap-3 p-4 bg-card border border-border rounded-xl hover:border-violet-300 hover:shadow-sm transition-all text-left group">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform"
                          style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                          <Icon size={16} className="text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-foreground">{qa.label}</p>
                          <p className="text-xs text-muted-foreground">{qa.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Campaigns Tab ── */}
            {activeTab === 'campaigns' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {['all', 'draft', 'queued', 'inProcess', 'sent', 'archive'].map(s => (
                      <button key={s} onClick={() => setCampaignStatusFilter(s)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                          campaignStatusFilter === s ? 'text-white shadow-sm' : 'border border-border text-muted-foreground hover:bg-muted'
                        }`}
                        style={campaignStatusFilter === s ? { background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' } : {}}>
                        {s === 'all' ? 'All' : s === 'inProcess' ? 'Sending' : s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input className="pl-8 pr-3 py-1.5 border border-border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500 w-48"
                        placeholder="Search campaigns…" value={campaignSearch} onChange={e => setCampaignSearch(e.target.value)} />
                    </div>
                    <button onClick={() => setShowCreateCampaign(true)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white"
                      style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                      <Plus size={14} />
                      New Campaign
                    </button>
                  </div>
                </div>

                {filteredCampaigns.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Inbox size={32} className="text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">No campaigns found</p>
                    <button onClick={() => setShowCreateCampaign(true)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                      style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                      Create your first campaign
                    </button>
                  </div>
                ) : (
                  <div className="bg-card border border-border rounded-xl overflow-hidden">
                    <div className="divide-y divide-border">
                      {filteredCampaigns.map(c => {
                        const stats = c.statistics?.globalStats;
                        const isExpanded = expandedCampaign === c.id;
                        const openRate = stats && stats.delivered > 0 ? ((stats.uniqueViews / stats.delivered) * 100).toFixed(1) : null;
                        const ctr = stats && stats.delivered > 0 ? ((stats.uniqueClicks / stats.delivered) * 100).toFixed(1) : null;

                        return (
                          <div key={c.id}>
                            <div className="flex items-center justify-between px-5 py-3.5 hover:bg-muted/20 transition-colors cursor-pointer"
                              onClick={() => setExpandedCampaign(isExpanded ? null : c.id)}>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-semibold text-foreground truncate">{c.name}</p>
                                  <CampaignStatusBadge status={c.status} />
                                </div>
                                <p className="text-xs text-muted-foreground truncate mt-0.5">{c.subject}</p>
                              </div>
                              <div className="flex items-center gap-4 ml-4 flex-shrink-0">
                                {stats && (
                                  <div className="hidden lg:flex items-center gap-4 text-xs text-muted-foreground">
                                    <span className="flex items-center gap-1"><Send size={11} />{(stats.sent ?? 0).toLocaleString()} sent</span>
                                    {openRate && <span className="flex items-center gap-1 text-emerald-600"><TrendingUp size={11} />{openRate}% open</span>}
                                    {ctr && <span className="flex items-center gap-1 text-blue-600"><MousePointerClick size={11} />{ctr}% CTR</span>}
                                  </div>
                                )}
                                {(c.status === 'draft' || c.status === 'queued') && (
                                  <button onClick={e => { e.stopPropagation(); handleSendNow(c.id); }}
                                    disabled={sendingCampaignId === c.id}
                                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold text-white disabled:opacity-60"
                                    style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                                    {sendingCampaignId === c.id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                                    Send Now
                                  </button>
                                )}
                                {isExpanded ? <ChevronUp size={16} className="text-muted-foreground" /> : <ChevronDown size={16} className="text-muted-foreground" />}
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="px-5 pb-4 bg-muted/20 border-t border-border">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3">
                                  <div>
                                    <p className="text-xs text-muted-foreground">Sender</p>
                                    <p className="text-sm font-medium text-foreground">{c.sender?.name} &lt;{c.sender?.email}&gt;</p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-muted-foreground">Created</p>
                                    <p className="text-sm font-medium text-foreground">{new Date(c.createdAt).toLocaleDateString()}</p>
                                  </div>
                                  {c.scheduledAt && (
                                    <div>
                                      <p className="text-xs text-muted-foreground">Scheduled</p>
                                      <p className="text-sm font-medium text-foreground">{new Date(c.scheduledAt).toLocaleString()}</p>
                                    </div>
                                  )}
                                  {stats && (
                                    <>
                                      <div>
                                        <p className="text-xs text-muted-foreground">Delivered</p>
                                        <p className="text-sm font-medium text-emerald-600">{(stats.delivered ?? 0).toLocaleString()}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-muted-foreground">Soft Bounces</p>
                                        <p className="text-sm font-medium text-amber-600">{stats.softBounces ?? 0}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-muted-foreground">Hard Bounces</p>
                                        <p className="text-sm font-medium text-red-600">{stats.hardBounces ?? 0}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-muted-foreground">Unsubscribes</p>
                                        <p className="text-sm font-medium text-foreground">{stats.unsubscriptions ?? 0}</p>
                                      </div>
                                      <div>
                                        <p className="text-xs text-muted-foreground">Complaints</p>
                                        <p className="text-sm font-medium text-foreground">{stats.complaints ?? 0}</p>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Contacts Tab ── */}
            {activeTab === 'contacts' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input className="pl-8 pr-3 py-1.5 border border-border rounded-lg text-sm bg-background focus:outline-none focus:ring-2 focus:ring-violet-500 w-56"
                      placeholder="Search contacts…" value={contactSearch} onChange={e => setContactSearch(e.target.value)} />
                  </div>
                  <button onClick={() => setShowAddContact(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                    <Plus size={14} />
                    Add Contact
                  </button>
                </div>

                <div className="bg-card border border-border rounded-xl overflow-hidden">
                  <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                      {contactCount.toLocaleString()} total contacts
                    </p>
                  </div>
                  {filteredContacts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-2">
                      <Users size={28} className="text-muted-foreground/40" />
                      <p className="text-sm text-muted-foreground">No contacts found</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-border">
                      {filteredContacts.map(c => (
                        <div key={c.id} className="flex items-center justify-between px-5 py-3 hover:bg-muted/20 transition-colors">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-xs font-bold"
                              style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                              {(c.attributes?.FIRSTNAME?.[0] || c.email[0]).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {c.attributes?.FIRSTNAME || c.attributes?.LASTNAME
                                  ? `${c.attributes?.FIRSTNAME || ''} ${c.attributes?.LASTNAME || ''}`.trim()
                                  : c.email}
                              </p>
                              <p className="text-xs text-muted-foreground truncate">{c.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                            {c.emailBlacklisted && (
                              <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">Blacklisted</span>
                            )}
                            <span className="text-xs text-muted-foreground hidden md:block">
                              {new Date(c.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Lists Tab ── */}
            {activeTab === 'lists' && (
              <div className="space-y-4">
                <div className="flex justify-end">
                  <button onClick={() => setShowCreateList(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white"
                    style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                    <Plus size={14} />
                    New List
                  </button>
                </div>
                {lists.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <List size={32} className="text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">No contact lists yet</p>
                    <button onClick={() => setShowCreateList(true)}
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                      style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                      Create your first list
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {lists.map(l => (
                      <div key={l.id} className="bg-card border border-border rounded-xl p-4 hover:border-violet-300 hover:shadow-sm transition-all">
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                            style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                            <List size={16} className="text-white" />
                          </div>
                          <span className="text-xs text-muted-foreground">ID: {l.id}</span>
                        </div>
                        <p className="text-sm font-semibold text-foreground mb-1">{l.name}</p>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1"><Users size={11} />{l.totalSubscribers?.toLocaleString() ?? 0} subscribers</span>
                          {l.totalBlacklisted > 0 && <span className="text-red-500">{l.totalBlacklisted} blacklisted</span>}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">Created {new Date(l.createdAt).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Templates Tab ── */}
            {activeTab === 'templates' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">{templates.length} active templates from Brevo</p>
                  <a href="https://app.brevo.com/email-template/list" target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:bg-muted transition-colors">
                    <ExternalLink size={13} />
                    Manage in Brevo
                  </a>
                </div>
                {templates.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <FileText size={32} className="text-muted-foreground/40" />
                    <p className="text-sm text-muted-foreground">No active templates found</p>
                    <a href="https://app.brevo.com/email-template/list" target="_blank" rel="noopener noreferrer"
                      className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                      style={{ background: 'linear-gradient(135deg, #5b35e8, #8b5cf6)' }}>
                      Create templates in Brevo
                    </a>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map(t => (
                      <div key={t.id} className="bg-card border border-border rounded-xl p-4 hover:border-violet-300 hover:shadow-sm transition-all">
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                            style={{ background: 'linear-gradient(135deg, #0092ff, #00c4ff)' }}>
                            <FileText size={16} className="text-white" />
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {t.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-foreground mb-1 truncate">{t.name}</p>
                        <p className="text-xs text-muted-foreground truncate mb-2">{t.subject}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>From: {t.sender?.name}</span>
                          {t.tag && <span className="px-1.5 py-0.5 bg-muted rounded text-xs">{t.tag}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modals */}
      <CreateCampaignModal
        open={showCreateCampaign}
        onClose={() => setShowCreateCampaign(false)}
        onCreated={() => { fetchCampaigns(); setActiveTab('campaigns'); }}
        lists={lists}
      />
      <AddContactModal
        open={showAddContact}
        onClose={() => setShowAddContact(false)}
        onAdded={() => { fetchContacts(); setActiveTab('contacts'); }}
        lists={lists}
      />
      <CreateListModal
        open={showCreateList}
        onClose={() => setShowCreateList(false)}
        onCreated={() => { fetchLists(); setActiveTab('lists'); }}
      />
    </div>
  );
}
